import React, { useEffect, useState, useMemo } from "react";
import api from "../../../axiosConfig";
import { Button, Spinner, Alert, Card } from "react-bootstrap";
import * as XLSX from "xlsx";
import { popup } from "../../common/popup";

export default function HistoryMesinTab() {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [machineKeyword, setMachineKeyword] = useState("");

  useEffect(() => {
    fetchHistories();
  }, []);
  const hitungDurasiMenit = (start, end) => {
    if (!start || !end) return "-";

    const startTime = new Date(start);
    const endTime = new Date(end);

    const diffMs = endTime - startTime; // selisih ms
    const diffMinutes = Math.floor(diffMs / 1000 / 60);

    return diffMinutes >= 0 ? `${diffMinutes} menit` : "-";
  };

  const fetchHistories = async () => {
    try {
      setLoading(true);
      const res = await api.get("/maintenance/history-final");
      setHistories(res.data || []);
    } catch (err) {
      console.error(err);
      setError("❌ Gagal mengambil data history mesin");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     LIST MESIN (UNIK)
  ========================= */
  const machines = useMemo(() => {
    const map = new Map();
    histories.forEach((h) => {
      if (!map.has(h.machine_code)) {
        map.set(h.machine_code, {
          machine_code: h.machine_code,
          machine_name: h.machine_name,
          sector_name: h.sector_name,
        });
      }
    });
    return Array.from(map.values());
  }, [histories]);

  /* =========================
     HISTORY MESIN TERPILIH
  ========================= */
  const machineHistories = useMemo(() => {
    if (!selectedMachine) return [];
    return histories.filter(
      (h) => h.machine_code === selectedMachine.machine_code,
    );
  }, [histories, selectedMachine]);

  /* =========================
     FILTER TANGGAL (PER MESIN)
  ========================= */
  const filteredHistories = useMemo(() => {
    return machineHistories.filter((h) => {
      const historyDate = new Date(h.created_at);

      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && historyDate < start) return false;
      if (end) {
        end.setHours(23, 59, 59, 999); // biar full 1 hari
        if (historyDate > end) return false;
      }

      return true;
    });
  }, [machineHistories, startDate, endDate]);

  const filteredMachines = useMemo(() => {
    if (!machineKeyword.trim()) return machines;

    const keyword = machineKeyword.toLowerCase();

    return machines.filter(
      (m) =>
        m.machine_name?.toLowerCase().includes(keyword) ||
        m.machine_code?.toLowerCase().includes(keyword) ||
        m.sector_name?.toLowerCase().includes(keyword),
    );
  }, [machines, machineKeyword]);

  /* =========================
     EXPORT EXCEL (PER MESIN)
  ========================= */
  const exportToExcel = () => {
    if (filteredHistories.length === 0) {
      popup.alert("Data masih kosong");
      return;
    }

    const excelData = filteredHistories.map((h, i) => ({
      No: i + 1,
      Tanggal: new Date(h.created_at).toLocaleString(),
      Mesin: h.machine_name,
      Kode: h.machine_code,
      Sektor: h.sector_name,
      Gejala: h.gejala_masalah || "-",
      Uraian: h.uraian_masalah || "-",
      Tindakan: h.tindakan_penyebab || "-",
      Perbaikan: h.evakuasi_perbaikan || "-",
      Start: new Date(h.start_time).toLocaleString(),
      End: h.end_time ? new Date(h.end_time).toLocaleString() : "-",
      Downtime: h.downtime_minutes || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "History Mesin");
    XLSX.writeFile(wb, `History_${selectedMachine.machine_name}.xlsx`);
  };

  /* =========================
     LOADING / ERROR
  ========================= */
  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  /* =========================
     VIEW 1: LIST MESIN
  ========================= */
  if (!selectedMachine) {
    return (
      <div
        className="shortlife-container"
        style={{ marginTop: "-25px", height: "calc(108vh - 120px)" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">DAFTAR HISTORY MESIN</h2>

          <div style={{ width: "300px" }}>
            <input
              type="text"
              className="form-control"
              placeholder="🔍 Cari Mesin"
              value={machineKeyword}
              onChange={(e) => setMachineKeyword(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper w-100">
          <table className="preview-table">
            <thead>
              <tr>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Nama Mesin
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Kode</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Sektor</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {machines.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    Data belum ada
                  </td>
                </tr>
              ) : (
                machines.map((m, i) => (
                  <tr key={m.machine_code}>
                    <td className="text-center">{i + 1}</td>
                    <td className="cell-left">{m.machine_name}</td>
                    <td>{m.machine_code}</td>
                    <td>{m.sector_name}</td>
                    <td className="text-center">
                      <Button size="sm" onClick={() => setSelectedMachine(m)}>
                        Lihat History
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* =========================
     VIEW 2: HISTORY MESIN
  ========================= */
  return (
    <div className="shortlife-container">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h2 style={{ fontSize: "30px" }}>
            HISTORY MESIN <br></br>{" "}
            <strong>{selectedMachine.machine_name}</strong>
          </h2>
        </div>

        <div>
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div>
              <label className="form-label">Dari Tanggal</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Sampai Tanggal</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <Button
              variant="outline-danger"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
            >
              Reset
            </Button>
            <Button
              className="gap-2"
              variant="secondary"
              onClick={() => {
                setSelectedMachine(null);
                setStartDate("");
                setEndDate("");
              }}
            >
              ⬅ Kembali
            </Button>
            <Button variant="success" onClick={exportToExcel}>
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="preview-table">
          <thead>
            <tr>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Tanggal</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>No LPPM</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Masalah</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Penyelesaian
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Selesai perbaikan
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Waktu Perbaikan
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Waktu Stop Mesin
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>PIC</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Keterangan
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredHistories.length === 0 ? (
              <tr>
                <td colSpan="10">Belum ada history</td>
              </tr>
            ) : (
              filteredHistories.map((h, i) => (
                <tr key={h.id}>
                  <td className="text-center">{i + 1}</td>
                  <td className="text-center">
                    {new Date(h.created_at).toLocaleDateString("id-ID")}
                  </td>
                  <td className="text-center">
                    {i + 1},{new Date(h.created_at).toLocaleDateString("id-ID")}
                  </td>
                  <td>{h.gejala_masalah || "-"}</td>
                  <td>{h.evakuasi_perbaikan || "-"}</td>
                  <td className="text-center">
                    {h.end_time
                      ? new Date(h.end_time).toLocaleString("id-ID", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"}
                  </td>
                  <td className="text-center">
                    {hitungDurasiMenit(h.start_time, h.end_time)}
                  </td>

                  <td className="text-center">
                    {new Date(h.start_time).toLocaleTimeString("id-ID", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="text-center">{h.dikerjakan_oleh || "-"}</td>
                  <td>{h.keterangan || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
